import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check } from "lucide-react";
import { api } from "@/lib/api";
import { LogoMark } from "@/components/Shell";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [credits, setCredits] = useState(null);
  const [trial, setTrial] = useState(false);
  const qc = useQueryClient();
  const { data: pricing } = useQuery({ queryKey: ["pricing"], queryFn: async () => (await api.get("/billing/pricing")).data });
  const support = pricing?.support_email;

  useEffect(() => {
    if (!sessionId) { setStatus("missing"); return; }
    let attempts = 0;
    let timer;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setCredits(data.credits); setTrial(!!data.trial); setStatus("paid"); ["home", "ledger", "activity"].forEach((k) => qc.invalidateQueries({ queryKey: [k] })); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setStatus("failed"); return; }
      } catch { /* keep polling */ }
      if (attempts < 15) timer = setTimeout(poll, 2000); else setStatus("timeout");
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, qc]);

  return (
    <div className="min-h-screen flex items-center justify-center px-5 bg-background" data-testid="payment-success">
      <div className="max-w-md w-full text-center space-y-6">
        <LogoMark className="h-10 w-10 mx-auto" />
        {status === "checking" && <><h1 className="font-display text-3xl">Confirming your payment…</h1><p className="text-sm text-muted-foreground">One moment while Stripe confirms.</p></>}
        {status === "paid" && <><span className="mx-auto h-14 w-14 rounded-full bg-gold text-black flex items-center justify-center"><Check className="h-6 w-6" /></span><h1 className="font-display text-3xl" data-testid="payment-paid">{trial ? "Your free trial has started." : credits ? `${credits.toLocaleString()} credits added.` : "Payment confirmed."}</h1><p className="text-sm text-muted-foreground">{trial ? `${credits} trial credits are in your balance. Your plan's monthly credits arrive with the first payment.` : "Thank you. Your balance is updated and ready to use."}</p></>}
        {status === "failed" && <><h1 className="font-display text-3xl">Payment didn't complete.</h1><p className="text-sm text-muted-foreground">Payment was not confirmed. Check your billing status before retrying{support ? <>, or email <a href={`mailto:${support}`} className="underline underline-offset-4">{support}</a></> : null}.</p></>}
        {(status === "timeout" || status === "missing") && <><h1 className="font-display text-3xl">Still confirming.</h1><p className="text-sm text-muted-foreground">If your bank approved it, credits will appear within a few minutes{support ? <>. Still missing? Email <a href={`mailto:${support}`} className="underline underline-offset-4" data-testid="payment-support">{support}</a></> : null}.</p></>}
        <div className="flex justify-center gap-3"><Link to="/home" className="btn-gold inline-flex" data-testid="payment-back-home">Back to Home</Link><Link to="/billing" className="btn-outline inline-flex" data-testid="payment-billing">Billing</Link></div>
      </div>
    </div>
  );
}
